import CloseIcon from "@mui/icons-material/Close";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  capitalize,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import currency from "currency.js";
import type React from "react";
import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { LoadingBox } from "@/components/ui";
import { useApiQuery, useMe } from "../hooks";
import { useMemberInfo } from "../hooks/use-member-info";
import { useUsageLines } from "../hooks/use-usage";
import type { Usage, UsageInfo, UsageTarget } from "../types/usage";
import { getCurrencySymbol } from "../utils/money";
import { formatBigNumber, formatNumber, formatPercentage } from "../utils/numbers";
import { humanizeText } from "../utils/text";
import { AnimatedNumber } from "./animated-number";

// Lazy load the GraphTab component (contains recharts which is heavy)
const GraphTab = lazy(() => import("./usage-graph-tab"));

const getUsageTargetInfo = (target: UsageTarget) => {
  switch (target) {
    case "test_suite_run":
      return "Test Suite run";
    case "test_run":
      return "Test run";
    case "test_run.auto_fix":
      return "Test run with auto-fix";
    case "test_run.ai_step":
      return "Executed AI step in test run";
    case "generate.step":
      return "AI step";
    case "generate.step.max":
      return "(MAX) AI step";
    case "generate.step.ultra":
      return "(Ultra MAX) AI step";
    case "search":
      return "Search";
    case "search.max":
      return "(MAX) Search";
    case "search.ultra":
      return "(Ultra MAX) Search";
    case "discovery_run":
      return "Discovery run";
    case "scenario":
      return "Scenario";
    case "test_chat":
      return "New chat";
    case "raw_ai_query":
      return "Raw AI query";
    case "agent.stealth":
      return "Stealth agent";
    case "deduction":
      return "Promotion";
    case "check_schedule_run":
      return "Check schedule run";
    case "check.ai_validation":
      return "Check AI validation";
    case "check.ai_validation.max":
      return "(MAX) Check AI validation";
    case "check.ai_validation.ultra":
      return "(Ultra MAX) Check AI validation";
    case "argus_session":
      return "Argus session";
    case "argus_session.step":
      return "Argus step";
    case "browser_session":
      return "Browser session";
    case "check_run":
      return "Check run";
    default:
      return humanizeText(target);
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`usage-tabpanel-${index}`}
      aria-labelledby={`usage-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// History Tab Component
const HistoryTab: React.FC<{
  getMember: (id: string) => { name: string };
  onNavigate: (path: string) => void;
  onClose: () => void;
}> = ({ getMember, onNavigate, onClose }) => {
  const {
    data: linesData,
    isLoading,
    currentPage,
    totalPages,
    goToPage,
  } = useUsageLines({ initialLimit: 20 });

  const getUsageLineLink = useCallback(
    (line: Usage) => {
      if (!line.usage_target_id) return null;
      let link: string | null = null;

      switch (line.usage_target) {
        case "test_suite_run":
          link = `/suite-runs/${line.usage_target_id}`;
          break;
        case "test_run":
        case "test_run.auto_fix":
        case "test_run.ai_step":
          link = `/runs/${line.usage_target_id}`;
          break;
        case "discovery_run":
          link = `/discovery-runs/${line.usage_target_id}`;
          break;
        case "scenario":
          link = `/tests-main/scenarios/${line.usage_target_id}`;
          break;
        case "test_chat":
          link = `/test-chats/${line.usage_target_id}`;
          break;
        case "argus_session":
        case "argus_session.step":
          link = `/argus?session=${line.usage_target_id}`;
          break;
        default:
          return null;
      }

      return (
        <Button
          size="small"
          variant="text"
          color="inherit"
          onClick={() => {
            onClose();
            onNavigate(link);
          }}
        >
          View
        </Button>
      );
    },
    [onNavigate, onClose],
  );

  if (isLoading) {
    return <LoadingBox size={24} sx={{ my: 2, p: 0 }} />;
  }

  const lines = linesData?.items || [];

  return (
    <Stack spacing={2}>
      {linesData && (
        <Typography variant="body2" color="text.secondary">
          Showing {lines.length} of {linesData.total} usage records
        </Typography>
      )}

      {lines.length > 0 ? (
        <Stack
          direction="column"
          spacing={1}
          sx={{ maxHeight: 350, overflowY: "auto" }}
          divider={<Divider orientation="horizontal" flexItem />}
        >
          {lines.map((line) => (
            <Stack key={line.id} direction="column" spacing={0}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="body2">
                  {getUsageTargetInfo(line.usage_target)}{" "}
                  {line.usage_value < 0 ? (
                    <Chip
                      label={`+${Math.abs(line.usage_value)} credits`}
                      size="small"
                      color="success"
                      sx={{ ml: 1 }}
                    />
                  ) : (
                    `used ${line.usage_value} credits`
                  )}
                </Typography>
                {getUsageLineLink(line)}
              </Stack>
              {line.description && (
                <Typography variant="caption" color="text.secondary">
                  {line.description}
                </Typography>
              )}
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Used by <b>{getMember(line.used_by).name}</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(line.used_at).toLocaleString()}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No usage records found.
        </Typography>
      )}

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={currentPage + 1}
            onChange={(_, page) => goToPage(page - 1)}
            size="small"
          />
        </Box>
      )}
    </Stack>
  );
};

export const UsageInfoStack = () => {
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const nav = useNavigate();

  const { data: me } = useMe();
  const { getMember } = useMemberInfo();

  const { data, isLoading } = useApiQuery<UsageInfo>("/usage");

  // Effective limit = base limit + promotional deductions
  const effectiveLimit = useMemo(() => {
    if (!data) return 0;
    return data.period_usage_limit + (data.period_deductions || 0);
  }, [data?.period_usage_limit, data?.period_deductions, data]);

  const pct = useMemo(() => {
    if (!data) {
      return 0;
    }

    if (!!data?.plan && data?.plan.state === "trial") {
      return 0;
    }

    if (!data.period_usage || !effectiveLimit) {
      return 0;
    }

    if (effectiveLimit === 0) {
      return 0;
    }

    return data.period_usage / effectiveLimit;
  }, [data?.period_usage, data?.plan, effectiveLimit, data]);

  const price = useMemo(() => {
    if (!data?.plan || data?.plan.period_price_cents === 0) {
      return null;
    }

    return currency(data.plan.period_price_cents, {
      fromCents: true,
      precision: 2,
      symbol: getCurrencySymbol("en-US", data.plan.period_price_currency),
    });
  }, [data?.plan]);

  if (isLoading) {
    return <CircularProgress size={18} />;
  }

  if (!data) {
    return null;
  }

  return (
    <Box>
      <Tooltip title="View your usage details">
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => setOpen(true)}
          aria-label="View your usage details"
          fullWidth
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            fontSize: "14px",
            fontWeight: 500,
            borderRadius: "6px",
            cursor: "pointer",
            color: open ? "text.primary" : "text.secondary",
            backgroundColor: open ? "grey.50" : "transparent",
            border: open ? "1px solid" : "1px solid transparent",
            borderColor: open ? "divider" : "transparent",
            transition: "all 0.2s ease",
            "&:hover": {
              color: "text.primary",
              backgroundColor: "grey.50",
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" width="100%">
            <DataUsageIcon
              fontSize="small"
              sx={{ marginRight: "12px", width: "16px", height: "16px" }}
            />
            <Typography variant="subtitle2">
              <AnimatedNumber value={data.period_usage} /> / {formatBigNumber(effectiveLimit, 2)}
            </Typography>
          </Stack>
        </Button>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="usage-dialog-title"
        aria-describedby="usage-dialog-description"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="span">
              Your usage
            </Typography>
            <IconButton
              size="small"
              color="inherit"
              onClick={() => {
                setOpen(false);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={1} mb={2}>
            {/* Plan Info Section */}
            {data.plan && (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                divider={<Typography variant="subtitle1">·</Typography>}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={capitalize(data.plan.state)}
                    size="small"
                    color={
                      data.plan.state === "active"
                        ? "success"
                        : data.plan.state === "trial"
                          ? "secondary"
                          : "error"
                    }
                    variant="filled"
                  />
                  <Typography variant="subtitle1">{data.plan.name} plan</Typography>
                </Stack>
                <Typography variant="subtitle1">
                  {formatBigNumber(data.plan.allowed_usage, 2)} credits {data.plan.period}
                </Typography>
                {price && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      textDecoration: data.plan.state === "trial" ? "line-through" : "none",
                    }}
                  >
                    {price.format()}
                  </Typography>
                )}
              </Stack>
            )}

            {/* Billing Period Info */}
            {data.plan?.valid_from && data.plan?.valid_until && (
              <Stack
                direction="row"
                spacing={2}
                sx={{ bgcolor: "action.hover", p: 1, borderRadius: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Billing period:</strong>{" "}
                  {new Date(data.plan.valid_from).toLocaleDateString()} -{" "}
                  {new Date(data.plan.valid_until).toLocaleDateString()}
                </Typography>
              </Stack>
            )}

            {/* Usage Summary */}
            <Stack direction="column" spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Tooltip title={`Usage for this ${data.period} period`}>
                  <Typography variant="body2">
                    Usage: {formatNumber(data.period_usage)} /{" "}
                    {data.plan?.state === "trial" ? "∞" : formatNumber(effectiveLimit)} (
                    {formatPercentage(pct)})
                  </Typography>
                </Tooltip>
              </Stack>
              {data.period_deductions > 0 && (
                <Typography variant="caption" color="success.main">
                  Includes +{formatNumber(data.period_deductions)} promotional credits
                </Typography>
              )}
            </Stack>

            <Divider />

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                aria-label="usage tabs"
              >
                <Tab label="History" id="usage-tab-0" />
                <Tab label="Graph" id="usage-tab-1" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <HistoryTab getMember={getMember} onNavigate={nav} onClose={() => setOpen(false)} />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Suspense fallback={<LoadingBox size={24} sx={{ my: 2, p: 0 }} />}>
                <GraphTab />
              </Suspense>
            </TabPanel>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const orgId = me?.organization?.id || "";
              const name = me?.name || "";

              window.open(
                `mailto:t@desplega.ai?subject=Request%20more%20usage&body=Hello%20Desplega,%0A%0AI%20am%20${name}%20from%20organization%20${orgId}.%0A%0AI%20would%20like%20to%20request%20more%20usage.%0A%0AThank you!`,
                "_blank",
              );
            }}
            color="primary"
            variant="contained"
            size="small"
          >
            Request more
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
